// @ts-strict-ignore
import { requestElevatedAccess } from "@lib/elevatedAccess";
import { FetchElevatedAccessError, FetchRequestError } from "@lib/errors";
import { getRootErrorBoundary } from "@lib/rootErrorBoundary";

import { enhanceFetchArgs } from "./utils/enhanceFetchArgs";
import { extractError } from "./utils/extractError";
import { isFetchErrorModalSuppressedForSignal } from "./fetchErrorModalSuppression";
import { impersonationFetch } from "./impersonationFetch";
import type { FetchMethod, FetchOptions, SettingsOptions } from "./types";

export const fetchBase = async (
  url: string,
  method: FetchMethod,
  params: Record<string, any> = {},
  options: FetchOptions = {},
  settings: SettingsOptions = {
    setContentType: true,
    returnDetailedError: false,
    returnFetchRequestError: false,
    fetchBlobType: false,
    propagateFetchRequestError: true,
  },
  retryOnElevatedAccessError = true
): Promise<Response> => {
  const [fetchUrl, fetchOptions] = enhanceFetchArgs(url, method, params, options, settings);
  const {
    returnDetailedError = false,
    returnFetchRequestError = false,
    propagateFetchRequestError = true,
  } = settings;

  const response = await impersonationFetch(fetchUrl, fetchOptions);

  if (!response.ok) {
    if (returnDetailedError) {
      if (!returnFetchRequestError) {
        let detailedError: string;
        try {
          detailedError = await response.text();
          // eslint-disable-next-line no-empty
        } catch {}
        throw new Error(`${response.statusText}: ${detailedError ? detailedError : ""}`);
      }
    }
    const error = await extractError(response);

    if (error instanceof FetchElevatedAccessError && retryOnElevatedAccessError) {
      try {
        const accessObtained = await requestElevatedAccess();
        if (accessObtained) {
          return fetchBase(url, method, params, options, settings, false);
        }
      } catch {
        // failed to obtain elevated access -- fall through to error handling logic below
      }
    }

    // because the errors thrown by fetch are Promises, they do not trigger the normal error boundary
    // unless explicitly thrown by the component that uses them. In addition, @lib/fetch can and is
    // used by legacy coffeescript code. The original $.ajax overrides for knockout/jquery display
    // a modal on server errors. This code is designed to enable that same behavior with @lib/fetch
    // but using a Beacon modal instead of a jquery UI modal.
    //
    // window.__rootErrorBoundary is set in the `ErrorBoundary` file `app/javascript/src/components/ErrorBoundary/ErrorBoundary.tsx`
    if (
      propagateFetchRequestError &&
      error instanceof FetchRequestError &&
      !isFetchErrorModalSuppressedForSignal(options.signal)
    ) {
      getRootErrorBoundary()?.renderUserModal(error);
    }
    throw error;
  }

  return response;
};
